import { Paper } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { LoadingOverlay, notify } from "@dehub/theme";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import "dayjs/locale/mn";
import "dayjs/locale/ko";
import "dayjs/locale/zh-cn";
import { AuthApi, GeneralApi } from "./apis";
import { Layout } from "./components/layout";
import { usePermission } from "./components/permission";
import { RequirePermission } from "./components/permission/RequirePermission";
import { onMessageListener, requestForToken } from "./firebase/fcm-service";
import { IAuth } from "./interfaces/IAuth";
import { Access } from "./pages/access";
import { BankCallback } from "./pages/bank-callback";
import { SettlementDetails } from "./pages/account-balance/history-invoiced";
import { ARLedgerPage } from "./pages/account-balance/ar-ledger";
import { LedgerHubPage } from "./pages/ledger/hub";
import { LedgerLayout } from "./pages/ledger/LedgerSectionNav";
import { LedgerAccountsPage } from "./pages/ledger/accounts";
import { LedgerEventsPage } from "./pages/ledger/events";
import { LedgerMirrorPage } from "./pages/ledger/mirror";
import { LedgerIntercompanyPage } from "./pages/ledger/intercompany";
import { LedgerMemoPage } from "./pages/ledger/memo";
import { LedgerJournalPage } from "./pages/ledger/journal";
import { LedgerTemplatesPage } from "./pages/ledger/templates";
import { LedgerTrialBalancePage } from "./pages/ledger/reports/trial-balance";
import { LedgerBalanceSheetPage } from "./pages/ledger/reports/balance-sheet";
import { LedgerIncomeStatementPage } from "./pages/ledger/reports/income-statement";
import { LedgerAccountStatementPage } from "./pages/ledger/reports/account-statement";
import { LedgerArAgingPage } from "./pages/ledger/reports/ar-aging";
import { LedgerPeriodsPage } from "./pages/ledger/periods";
import { LedgerOpeningPage } from "./pages/ledger/opening";
import { LedgerSettingsPage } from "./pages/ledger/settings";
import { InvoiceDetails } from "./pages/buyer-invoice/invoice-details";
import { BuyerInvoiceList } from "./pages/buyer-invoice/list";
import { ClosedInvoiceDetails } from "./pages/closed-invoice/details";
import { ClosedInvoiceList } from "./pages/closed-invoice/list";
import { BillingDetails } from "./pages/customer-billing/billing-details";
import { SettlementInvoiceRedirect } from "./pages/account-balance/invoice-redirect";
import { BillingHistory } from "./pages/customer-billing/history-invoiced";
import { CustormerBilling } from "./pages/customer-billing/list";
import { Dashboard } from "./pages/dashboard/dashboard";
import { NotFound } from "./pages/not-found";
import { SalesInvoiceAmend } from "./pages/sales-invoice/amend";
import { SalesInvoiceDetails } from "./pages/sales-invoice/details";
import { SalesInvoiceList } from "./pages/sales-invoice/list";
import { SalesInvoiceForms } from "./pages/sales-invoice/new";
import { OurBuyerDetails } from "./pages/sales-invoice/our-buyer";
import { ReturnRequestCreate } from "./pages/refund-invoice/create";
import { ReturnDetails } from "./pages/refund-invoice/details";
import { RefundInvoiceList } from "./pages/refund-invoice/list";
import { Settings } from "./pages/settings";
import { StatementCreate } from "./pages/settlement/create";
import { StatementDetails } from "./pages/settlement/details";
import { StatementList } from "./pages/settlement/list";
import { ReconciliationRedirect } from "./pages/settlement/redirects";
import { Test } from "./pages/test";
import { authMe } from "./store/Auth";
import { initGeneral } from "./store/General";
import { message } from "./utils/Message";

let __WINDOW_LOADED__ = false;

const DAYJS_LOCALE_MAP: Record<string, string> = { mn: "mn", en: "en", ko: "ko", zh: "zh-cn" };

function isAccessPath(): boolean {
  return window.location.pathname.replace(/\/$/, "").endsWith("/access");
}

function App() {
  // Start true (except /access) so Layout never mounts → unmounts → remounts
  // around init(). With useState(false) + the sync setLoading(false) that used
  // to sit after the IIFE, Layout painted once, then remounted — every SWR
  // under Layout (notif, badges, ledger, lists) fired twice.
  const [loading, setLoading] = React.useState(() => !isAccessPath());
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const datesLocale = DAYJS_LOCALE_MAP[i18n.language] || "en";
  const { accessToken, myBusinessType } = useSelector((state: { auth: IAuth }) => state.auth);
  const { hasAccess, accessDenied } = usePermission("INV_DASH");
  const INV_NETWORK_SETTLEMENT_PAYMENT_HISTORY = usePermission("INV_NETWORK_SETTLEMENT_PAYMENT_HISTORY");

  const hasRequestedToken = useRef(false);

  // Firebase
  useEffect(() => {
    if (!hasRequestedToken.current) {
      requestForToken();
      hasRequestedToken.current = true;
    }

    onMessageListener((payload: any) => {
      notify.info({
        id: `notif-${Math.random()}`,
        title: payload?.notification?.title || "New Notification",
        message: payload?.notification?.body || "You have received a new notification.",
        autoClose: 6000,
      });
    });
  }, []);

  const init = React.useCallback(async () => {
    setLoading(true);
    try {
      if (accessToken) {
        const res = await GeneralApi.init();
        dispatch(initGeneral(res));
        const auth = await AuthApi.me();
        dispatch(authMe(auth));
      }
    } catch (error: any) {
      message.error(error?.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    // Skip init on /access — Access page dispatches the fresh token from URL
    // and runs init itself. Using the stale persisted token here causes a 401
    // → login redirect → bounce back to auth-admin.
    if (__WINDOW_LOADED__) return;
    __WINDOW_LOADED__ = true;
    if (isAccessPath()) {
      setLoading(false);
      return;
    }
    void init();
  }, [init]);

  if (loading) return <LoadingOverlay visible={loading} />;
  return (
      <DatesProvider settings={{ locale: datesLocale }}>
      <Routes>
        {/* /access bootstraps auth from the URL token — must render outside
            Layout so Layout's Header doesn't fire protected API calls with
            the stale persisted token and bounce us back to auth-admin on 401. */}
        <Route path="/access" element={<Access />} />
        {/* Bank/OAuth redirect target — outside Layout so the popup can postMessage + close. */}
        <Route path="/bank-callback" element={<BankCallback />} />
        <Route element={<Layout />}>
          <Route path="/" element={hasAccess ? <Dashboard /> : <Paper p={"xs"}>{accessDenied()}</Paper>} />

          {/* Нээлттэй нэхэмжлэх */}
          {myBusinessType === "SUPPLIER" && (
            <>
              <Route path="/sales-invoice" element={<SalesInvoiceList />} />
              <Route path="/sales-invoice/:id" element={<SalesInvoiceForms />} />
              <Route path="/sales-invoice/:id/details" element={<SalesInvoiceDetails />} />
              <Route path="/sales-invoice/:id/amend" element={<SalesInvoiceAmend />} />
              <Route path="/sales-invoice/:id/our-buyer" element={<OurBuyerDetails />} />
              <Route path="/settings" element={<RequirePermission code="INV_LIST"><Settings /></RequirePermission>} />
            </>
          )}

          {/* Нээлттэй нэхэмжлэх */}
          {myBusinessType === "BUYER" && (
            <>
              <Route path="/buyer-invoice" element={<BuyerInvoiceList />} />
              <Route path="/buyer-invoice/:id/:action" element={<InvoiceDetails />} />
            </>
          )}

          <Route path="/closed-invoice" element={<ClosedInvoiceList />} />
          <Route path="/closed-invoice/:id/details" element={<ClosedInvoiceDetails />} />

          {/* Буцаалт */}
          <Route path="/refund-invoice" element={<RefundInvoiceList />} />
          <Route path="/refund-invoice/new" element={<RequirePermission code="RETURN_CREATE"><ReturnRequestCreate /></RequirePermission>} />
          <Route path="/refund-invoice/:id" element={<RequirePermission code="RETURN_VIEW"><ReturnDetails /></RequirePermission>} />

          <Route path="/ar-ledger" element={<Navigate to="/ledger/ar" replace />} />

          <Route path="/ledger" element={<LedgerLayout />}>
            <Route index element={<LedgerHubPage />} />
            <Route path="ar" element={<ARLedgerPage />} />
            <Route path="accounts" element={<LedgerAccountsPage />} />
            <Route path="templates" element={<LedgerTemplatesPage />} />
            <Route path="journal" element={<LedgerJournalPage />} />
            <Route path="events" element={<LedgerEventsPage />} />
            <Route path="mirror" element={<LedgerMirrorPage />} />
            <Route path="intercompany" element={<LedgerIntercompanyPage />} />
            <Route path="memo" element={<LedgerMemoPage />} />
            <Route path="reports/trial-balance" element={<LedgerTrialBalancePage />} />
            <Route path="reports/balance-sheet" element={<LedgerBalanceSheetPage />} />
            <Route path="reports/income-statement" element={<LedgerIncomeStatementPage />} />
            <Route path="reports/account-statement" element={<LedgerAccountStatementPage />} />
            <Route path="reports/ar-aging" element={<LedgerArAgingPage />} />
            <Route path="periods" element={<LedgerPeriodsPage />} />
            <Route path="opening" element={<LedgerOpeningPage />} />
            <Route path="settings" element={<LedgerSettingsPage />} />
          </Route>

          {/* Тооцоо нийлэх = тооцооны хуудас (RS). Хуучин settlement мөр зөвхөн /settlement/legacy/:id-д унших горимоор. */}
          <Route path="/settlement" element={<StatementList />} />
          <Route path="/settlement/new" element={<StatementCreate />} />
          <Route path="/settlement/legacy/:id" element={<SettlementDetails readOnly />} />
          <Route path="/settlement/:id" element={<StatementDetails />} />
          <Route path="/settlement/:id/history" element={<SettlementInvoiceRedirect />} />

          <Route path="/customer-billing" element={<CustormerBilling />} />
          <Route path="/customer-billing/:id" element={<BillingDetails />} />
          <Route
            path="/customer-billing/:id/history"
            element={
              INV_NETWORK_SETTLEMENT_PAYMENT_HISTORY.hasAccess ? (
                <BillingHistory />
              ) : (
                <Paper p={"xs"}>{INV_NETWORK_SETTLEMENT_PAYMENT_HISTORY.accessDenied()}</Paper>
              )
            }
          />
          {/* Тооцооны хуудас */}
          <Route path="/reconciliation" element={<Navigate to="/settlement" replace />} />
          <Route path="/reconciliation/new" element={<Navigate to="/settlement/new" replace />} />
          <Route path="/reconciliation/:id" element={<ReconciliationRedirect />} />

          <Route path="/test" element={<Test />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </DatesProvider>
    );
}

export default App;
